import {
  ActionList,
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  Icon,
  Layout,
  Modal,
  Page,
  Pagination,
  Popover,
  ResourceList,
  Select,
  Spinner,
  Stack,
  Tabs,
  TextField,
  TextStyle,
  Toast,
} from "@shopify/polaris";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAdmin } from "../../context/admin";
import { fetchData } from "../../util/fetch";
import ErrorSummary from "../HdsImportHistoryPage/components/ErrorSummary";
import ImportDetails from "../HdsImportHistoryPage/components/ImportDetails";
import ImportStats from "../HdsImportHistoryPage/components/ImportStats";
import CsvPreviewModal from "./components/CsvPreviewModal";
import PosChangeSummary from "./components/PosChangeSummary";
import RollbackModal from "./components/RollbackModal";

const PosImportHistoryPage = () => {
  const { selectedShopId } = useAdmin();
  const [importHistories, setImportHistories] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedImport, setSelectedImport] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20,
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [previewImportId, setPreviewImportId] = useState(null);
  const [activePopoverId, setActivePopoverId] = useState(null);

  // Rollback states
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackImport, setRollbackImport] = useState(null);
  const [rollbackSelectedGids, setRollbackSelectedGids] = useState(null);
  const [toastContent, setToastContent] = useState(null);

  const isUnmounted = useRef(false);
  const provider = "globepos";

  const togglePopover = useCallback((id) => {
    setActivePopoverId((activeId) => (activeId === id ? null : id));
  }, []);

  const loadImportHistories = useCallback(
    async (page = 1) => {
      if (!selectedShopId) return;

      try {
        setLoading(true);
        fetchData(
          `/api/shops/${selectedShopId}/pos_import_histories?page=${page}&provider=${provider}`,
          (response) => {
            if (!isUnmounted.current) {
              setImportHistories(response.import_histories || []);
              setPagination((prev) => response.pagination || prev);
              setLoading(false);
            }
          },
          (err) => {
            if (!isUnmounted.current) {
              setError("Failed to load POS import histories");
              console.error(err);
              setLoading(false);
            }
          },
        );
      } catch (err) {
        if (!isUnmounted.current) {
          setError("Failed to load POS import histories");
          console.error(err);
          setLoading(false);
        }
      }
    },
    [provider, selectedShopId],
  );

  const loadStats = useCallback(() => {
    if (!selectedShopId) return;

    try {
      fetchData(
        `/api/shops/${selectedShopId}/pos_import_histories/stats?provider=${provider}`,
        (response) => {
          if (!isUnmounted.current) {
            setStats(response.stats);
          }
        },
        (err) => {
          if (!isUnmounted.current) {
            console.error("Failed to load POS import stats:", err);
          }
        },
      );
    } catch (err) {
      console.error("Failed to load POS import stats:", err);
    }
  }, [provider, selectedShopId]);

  useEffect(() => {
    if (selectedShopId) {
      loadImportHistories();
      loadStats();
    }
  }, [selectedShopId, loadImportHistories, loadStats]);

  // Auto-refresh while any imports are pending or processing
  useEffect(() => {
    if (!autoRefresh) return;
    const hasActive = importHistories.some((h) =>
      ["pending", "processing", "rollback_in_progress"].includes(h.status),
    );
    if (!hasActive) return;
    const t = setInterval(() => {
      loadImportHistories(pagination.current_page);
      loadStats();
    }, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, importHistories, pagination.current_page]);

  useEffect(() => {
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  const handleImportClick = async (importHistory) => {
    if (!selectedShopId) return;

    try {
      setDetailLoading(true);
      setModalOpen(true);
      setSelectedImport(null);
      setActiveModalTab(0);
      fetchData(
        `/api/shops/${selectedShopId}/pos_import_histories/${importHistory.id}`,
        (response) => {
          if (!isUnmounted.current) {
            setSelectedImport(response.import_history);
            setDetailLoading(false);
          }
        },
        (err) => {
          if (!isUnmounted.current) {
            setError("Failed to load import details");
            console.error(err);
            setDetailLoading(false);
            setModalOpen(false);
          }
        },
      );
    } catch (err) {
      if (!isUnmounted.current) {
        setError("Failed to load import details");
        console.error(err);
        setDetailLoading(false);
        setModalOpen(false);
      }
    }
  };

  const handleRollbackClick = (importHistory, e) => {
    if (e) e.stopPropagation();
    setRollbackImport(importHistory);
    setRollbackSelectedGids(null);
    setRollbackModalOpen(true);
    // Close detail modal if open
    setModalOpen(false);
  };

  const handleRevertSelected = (gids) => {
    setRollbackImport(selectedImport);
    setRollbackSelectedGids(gids);
    setRollbackModalOpen(true);
    setModalOpen(false);
  };

  const handleRollbackConfirm = async () => {
    if (!rollbackImport || !selectedShopId) return;

    try {
      setRollbackLoading(true);
      const headers = {
        "Content-Type": "application/json",
      };
      if (localStorage.auth_token) {
        headers["Authorization"] = `Bearer ${localStorage.auth_token}`;
      }

      let url = `/api/shops/${selectedShopId}/pos_import_histories/${rollbackImport.id}/rollback`;
      const body = {};

      if (rollbackSelectedGids && rollbackSelectedGids.length > 0) {
        url = `/api/shops/${selectedShopId}/pos_import_histories/${rollbackImport.id}/rollback_selective`;
        body.product_gids = rollbackSelectedGids;
      }

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Rollback failed");
      }

      setToastContent(`Rollback started for: ${rollbackImport.filename}`);
      setRollbackModalOpen(false);
      setRollbackImport(null);
      setRollbackSelectedGids(null);
      // Refresh list immediately
      loadImportHistories(pagination.current_page);
    } catch (err) {
      console.error("Rollback error:", err);
      setError(err.message || "Failed to rollback import");
      setRollbackModalOpen(false);
    } finally {
      setRollbackLoading(false);
    }
  };

  const handleDownload = async (importId, e) => {
    if (e) e.stopPropagation();
    if (!selectedShopId) return;

    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (localStorage.auth_token) {
        headers["Authorization"] = `Bearer ${localStorage.auth_token}`;
      }

      const response = await fetch(
        `/api/shops/${selectedShopId}/pos_import_histories/${importId}/download`,
        { headers },
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || `Failed to download file (${response.status})`);
        return;
      }

      const data = await response.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      if (!data.download_url) {
        setError("Download URL not available");
        return;
      }

      // Create a temporary link and click it to download
      const link = document.createElement("a");
      link.href = data.download_url;
      link.download = data.filename || "download.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed:", err);
      setError("Failed to download file. The file may no longer be available.");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { status: "success", text: "Completed" },
      failed: { status: "critical", text: "Failed" },
      processing: { status: "warning", text: "Processing" },
      pending: { status: "info", text: "Pending" },
      rolled_back: { status: "info", text: "Rolled Back" },
    };

    const config = statusConfig[status] || { status: "info", text: status };
    return config.text;
  };

  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const filtered = importHistories
    .filter((h) => (statusFilter === "all" ? true : h.status === statusFilter))
    .filter((h) =>
      search
        ? (h.filename || "").toLowerCase().includes(search.toLowerCase())
        : true,
    );

  const renderItem = (item) => {
    const {
      id,
      filename,
      status,
      started_at,
      duration_formatted,
      total_products_processed,
      products_created,
      products_updated,
      has_errors,
      error_count,
      gcp_file_id,
    } = item;

    const statusConfig = {
      completed: { status: "success", label: "Completed" },
      failed: { status: "critical", label: "Failed" },
      processing: { status: "warning", label: "Processing" },
      pending: { status: "info", label: "Pending" },
      rolled_back: { status: "info", label: "Rolled Back" },
    }[status] || { status: "info", label: status };

    const actions = [
      {
        content: "Preview",
        onAction: (e) => {
          setActivePopoverId(null);
          setPreviewImportId(item.id);
        },
      },
      {
        content: "Download",
        onAction: (e) => {
          setActivePopoverId(null);
          handleDownload(item.id);
        },
      },
      {
        content: "View Details",
        onAction: (e) => {
          setActivePopoverId(null);
          handleImportClick(item);
        },
      },
    ];

    if (item.can_rollback && status === "completed") {
      actions.push({
        content: "Rollback Import",
        destructive: true,
        onAction: (e) => {
          setActivePopoverId(null);
          handleRollbackClick(item);
        },
      });
    }

    return (
      <ResourceList.Item
        id={id}
        onClick={() => handleImportClick(item)}
        accessibilityLabel={`View details for ${filename}`}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1.5fr 1fr 1fr 200px",
            gap: "16px",
            alignItems: "center",
          }}
        >
          {/* File Info */}
          <div style={{ minWidth: 0 }}>
            <TextStyle variation="strong">{filename}</TextStyle>
            {gcp_file_id && (
              <div
                style={{
                  color: "#637381",
                  fontSize: "0.85em",
                  marginTop: "2px",
                }}
              >
                <TextStyle variation="subdued">ID: {gcp_file_id}</TextStyle>
              </div>
            )}
          </div>

          {/* Timing */}
          <div>
            <TextStyle variation="subdued">
              {new Date(started_at).toLocaleDateString()}{" "}
              {new Date(started_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </TextStyle>
            <div style={{ fontSize: "0.85em", color: "#637381" }}>
              Duration: {duration_formatted || "-"}
            </div>
          </div>

          {/* Status */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <Badge status={statusConfig.status}>{statusConfig.label}</Badge>
              {has_errors && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "2px 8px",
                    borderRadius: "10px",
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#dc2626",
                    fontSize: "0.75rem",
                    fontWeight: "500",
                  }}
                >
                  <span style={{ fontSize: "0.7rem" }}>⚠</span>
                  {error_count} {error_count === 1 ? "error" : "errors"}
                </span>
              )}
            </div>
          </div>

          {/* Results */}
          <div style={{ fontSize: "0.9em" }}>
            <div style={{ fontWeight: "600" }}>
              {item.skipped_duplicate ? (
                <span style={{ color: "#6366f1" }}>
                  Skipped (duplicate file)
                </span>
              ) : (
                <>{total_products_processed} products</>
              )}
            </div>
            <div style={{ color: "#637381", fontSize: "0.85em" }}>
              <span
                style={{ color: products_created > 0 ? "#008060" : "inherit" }}
              >
                {products_created} created
              </span>
              {" · "}
              <span
                style={{ color: products_updated > 0 ? "#916a00" : "inherit" }}
              >
                {products_updated} updated
              </span>
              {item.products_skipped > 0 && (
                <>
                  {" · "}
                  <span style={{ color: "#6366f1" }}>
                    {item.products_skipped} skipped
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div
            style={{
              textAlign: "right",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Popover
              active={activePopoverId === id}
              activator={
                <Button
                  plain
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePopover(id);
                  }}
                  disclosure
                >
                  Actions
                </Button>
              }
              onClose={() => setActivePopoverId(null)}
            >
              <ActionList items={actions} />
            </Popover>
          </div>
        </div>
      </ResourceList.Item>
    );
  };

  return (
    <Page
      title="POS Import History"
      subtitle="Review GlobePOS imports across your shops"
      primaryAction={{
        content: loading ? "Refreshing..." : "Refresh History",
        loading: loading,
        onAction: () => {
          loadImportHistories(pagination.current_page);
          loadStats();
        },
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner
              title="Error"
              status="critical"
              onDismiss={() => setError(null)}
            >
              {error}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <ImportStats stats={stats} />
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: "16px", borderBottom: "1px solid #dfe3e8" }}>
              <Stack wrap spacing="tight" alignment="center">
                <div style={{ flex: 1, minWidth: 280 }}>
                  <TextField
                    label="Search filename"
                    labelHidden
                    value={search}
                    onChange={setSearch}
                    autoComplete="off"
                    placeholder="Search by filename (e.g. grocerbox.csv)"
                    prefix={<Icon source="search" />}
                  />
                </div>
                <div style={{ minWidth: 200 }}>
                  <Select
                    label="Status"
                    labelHidden
                    options={[
                      { label: "All Statuses", value: "all" },
                      { label: "Completed", value: "completed" },
                      { label: "Failed", value: "failed" },
                      { label: "Processing", value: "processing" },
                      { label: "Pending", value: "pending" },
                    ]}
                    onChange={setStatusFilter}
                    value={statusFilter}
                  />
                </div>
                <Stack spacing="tight" alignment="center">
                  <Checkbox
                    label="Auto-refresh"
                    checked={autoRefresh}
                    onChange={setAutoRefresh}
                  />
                </Stack>
              </Stack>
            </div>

            <div
              style={{
                padding: "12px 16px",
                background: "#f6f6f7",
                borderBottom: "1px solid #dfe3e8",
                display: "grid",
                gridTemplateColumns: "2fr 1.5fr 1fr 1fr 200px",
                gap: "16px",
                fontWeight: "600",
                fontSize: "0.85em",
                color: "#637381",
                textTransform: "uppercase",
              }}
            >
              <div>File</div>
              <div>Imported At</div>
              <div>Status</div>
              <div>Results</div>
              <div style={{ textAlign: "right" }}>Action</div>
            </div>

            <ResourceList
              resourceName={{ singular: "import", plural: "imports" }}
              items={filtered}
              renderItem={renderItem}
              loading={loading}
              emptyState={
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <TextStyle variation="subdued">
                    No imports found matching your criteria.
                  </TextStyle>
                </div>
              }
            />

            {pagination.total_pages > 1 && (
              <div
                style={{
                  padding: "16px",
                  borderTop: "1px solid #dfe3e8",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Pagination
                  hasPrevious={pagination.current_page > 1}
                  onPrevious={() =>
                    loadImportHistories(pagination.current_page - 1)
                  }
                  hasNext={pagination.current_page < pagination.total_pages}
                  onNext={() =>
                    loadImportHistories(pagination.current_page + 1)
                  }
                  label={`Page ${pagination.current_page} of ${pagination.total_pages}`}
                />
              </div>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedImport(null);
        }}
        title={selectedImport ? selectedImport.filename : "Import Details"}
        large
      >
        <Modal.Section>
          {detailLoading || !selectedImport ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <Spinner size="large" />
            </div>
          ) : (
            <>
              <Tabs
                tabs={[
                  {
                    id: "overview",
                    content: "Overview",
                    panelID: "overview-panel",
                  },
                  {
                    id: "changes",
                    content: "Changes",
                    panelID: "changes-panel",
                  },
                  { id: "errors", content: "Errors", panelID: "errors-panel" },
                ]}
                selected={activeModalTab}
                onSelect={setActiveModalTab}
              >
                <Card.Section>
                  {activeModalTab === 0 && (
                    <ImportDetails
                      importHistory={selectedImport}
                      onRollback={() => handleRollbackClick(selectedImport)}
                    />
                  )}
                  {activeModalTab === 1 && (
                    <PosChangeSummary
                      importHistory={selectedImport}
                      onRevertSelected={handleRevertSelected}
                    />
                  )}
                  {activeModalTab === 2 && (
                    <ErrorSummary importHistory={selectedImport} />
                  )}
                </Card.Section>
              </Tabs>
            </>
          )}
        </Modal.Section>
      </Modal>

      <CsvPreviewModal
        open={!!previewImportId}
        onClose={() => setPreviewImportId(null)}
        importId={previewImportId}
        shopId={selectedShopId}
      />

      <RollbackModal
        open={rollbackModalOpen}
        onClose={() => setRollbackModalOpen(false)}
        onConfirm={handleRollbackConfirm}
        importHistory={rollbackImport}
        loading={rollbackLoading}
        selectedCount={rollbackSelectedGids ? rollbackSelectedGids.length : 0}
      />

      {toastContent && (
        <Toast content={toastContent} onDismiss={() => setToastContent(null)} />
      )}
    </Page>
  );
};

export default PosImportHistoryPage;
